import { DocumentStatus, UserRole } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloConfirmationDialog, PaloOverflowMenu } from '@synergy/components-palo';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { ReactNode, useState } from 'react';
import { hasRole } from '@synergy/utils';

import { DemandExportListModal } from '../../overview/components/demand-export-list-modal';
import { PaloDocumentPhase, PaloFlowAction } from '../../../../detail-header/palo-main-action';
import { transitionAvailableSelector } from '../store/demand-detail-selectors';
import { unlockToggle } from '../store/demand-detail-actions';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export interface PaloDemandDetailActionsProps {
    phase: PaloDocumentPhase;
    onTransition: () => void;
    /** What the demand itself offers, left of the menu. */
    flowActions?: ReactNode;
}

/**
 * What the demand offers beside saving.
 *
 * Locking and deleting have moved into the menu: deleting used to sit as a red
 * button in the same row as saving, one slip apart (#2206). Saving itself is
 * the mask's one filled button and no longer part of this row.
 */
export const PaloDemandDetailActions = (props: PaloDemandDetailActionsProps) => {
    const transitionAvailable = useAppSelector(transitionAvailableSelector);

    return (
        <>
            <PaloFlowAction
                phase={props.phase}
                kind="followUp"
                disabled={!transitionAvailable}
                onClick={props.onTransition}
            >
                <FormattedMessage id="app.menu.action.transition" />
            </PaloFlowAction>
            {props.flowActions}
        </>
    );
};

export interface PaloDemandDetailMenuProps {
    onDelete: () => void;
    onPreview?: () => void;
}

/** Locking and deleting: two clicks, never the one meant for saving. */
export const PaloDemandDetailMenu = ({ onDelete, onPreview }: PaloDemandDetailMenuProps) => {
    const dispatch = useThunkDispatch();
    const user = useAppSelector(state => state.user);
    const { status, id } = useAppSelector(state => state.demandDetail.overview.stepData.values);
    const unlocked = useAppSelector(state => state.demandDetail.common.unlocked);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [isDemandExportListOpen, setIsDemandExportListModalOpen] = useState(false);

    return (
        <>
            <PaloOverflowMenu
                actions={[
                    ...(onPreview
                        ? [
                              {
                                  label: <FormattedMessage id="app.common.previewPrint" />,
                                  disabled: !id || status === DocumentStatus.Created,
                                  onClick: onPreview,
                              },
                          ]
                        : []),
                    {
                        label: <FormattedMessage id="app.menu.action.exportList" />,
                        disabled: !id || status === DocumentStatus.Created,
                        onClick: () => setIsDemandExportListModalOpen(true),
                    },
                    ...(hasRole(UserRole.SuperAdmin, user) && status !== DocumentStatus.Created
                        ? [
                              {
                                  label: <FormattedMessage id={unlocked ? 'app.label.lock' : 'app.label.unlock'} />,
                                  onClick: () => dispatch(unlockToggle()),
                              },
                          ]
                        : []),
                    {
                        label: <FormattedMessage id="app.common.delete" />,
                        disabled: ![DocumentStatus.Created, DocumentStatus.Scheduled].includes(status),
                        danger: true,
                        onClick: () => setShowDeleteWarning(true),
                    },
                ]}
            />
            {id && (
                <PaloNextThemeProvider>
                    <DemandExportListModal
                        demandId={id}
                        open={isDemandExportListOpen}
                        onClose={() => setIsDemandExportListModalOpen(false)}
                    />
                </PaloNextThemeProvider>
            )}
            <PaloConfirmationDialog
                isVisible={showDeleteWarning}
                title={<FormattedMessage id="app.common.deleteHeader" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setShowDeleteWarning(false)}
                onConfirm={onDelete}
            >
                <FormattedMessage id="app.common.deleteWarning" />
            </PaloConfirmationDialog>
        </>
    );
};
